// ------------------------------------------------------------------------------
// name: usePhoneCode
// author: mudas
// created: 2025/4/1
// ------------------------------------------------------------------------------

import { useState } from 'react';
import { useCountDown, useLocalStorageState } from 'ahooks';

import { LoginInterface, MobileCode, VerCodeSendStatus } from 'common/types';
import service, { isSuccessCode } from 'common/service';
import { useComputed } from 'common/hooks';
import { isNil } from 'lodash-es';
import { FastApiStatusCode } from 'common/service/constants/fastApi';

const DURATION_SECOND = 120;
const INTERVAL = 1000;

/**
 * 手机验证码辅助
 * @param options.uid 若想独立使用不同的缓存 key，请指定唯一的 uid
 * @param options.interval 当获取验证码后冷却时间，倒计时的间隔时间（默认：1秒，单位：毫秒）
 * @param options.duration 倒计时总时间（默认：120秒，单位：秒）
 */
export const usePhoneCode = (options?: { uid?: string; interval?: number }) => {
  const { uid = 'PhoneCodeCountDownID', interval = INTERVAL } = options || {};
  const [loading, setLoading] = useState<boolean>();
  const [cacheTimeStamp, setCacheTimeStamp] = useLocalStorageState<Record<string, number | undefined>>(
    'phoneCodeTimeCache',
    {},
  );

  const [targetDate, setTargetDate] = useState<number>(); // 时间戳
  const [countdown, formatted] = useCountDown({ targetDate, interval });

  // 当前是否正在执行任务中
  const running = useComputed(() => loading || countdown > 0, [loading, countdown]);

  // 启动倒计时
  const startCountDown = (params: LoginInterface) => {
    const key = `${params.region}-${params.phone}-${params.scope}`;

    const targetTime = cacheTimeStamp?.[key];
    const currentTime = Date.now();
    if (!isNil(targetTime) && targetTime > currentTime) {
      // 如果缓存的时间大于当前时间，说明还在倒计时中
      setTargetDate(targetTime);
      return;
    }
    const newTargetTime = currentTime + DURATION_SECOND * 1000;
    setCacheTimeStamp((prev) => ({ ...prev, [key]: newTargetTime }));

    setTargetDate(newTargetTime);
  };

  // 停止倒计时
  const stopCountDown = (payload: LoginInterface) => {
    setTargetDate(undefined);

    setCacheTimeStamp((prev) => ({
      ...prev,
      [`${payload.region}-${payload.phone}-${payload.scope}`]: undefined,
    }));
  };

  // 发送手机验证码
  const sendCode = async (payload: LoginInterface) => {
    if (running) return;
    setLoading(true);
    stopCountDown(payload);

    try {
      const { code, message } = await service.userSetting.sendLoginPhoneCode({ ...payload, scope: 1 });

      if (code === FastApiStatusCode.CodeHasBeenSent) {
        startCountDown({ ...payload, scope: MobileCode.Login });
        return Promise.resolve({ msg: '该手机号验证码已发送', status: VerCodeSendStatus.TooFrequent });
      }
      startCountDown({ ...payload, scope: MobileCode.Login });
      setLoading(false);
      return Promise.resolve({ msg: message, status: VerCodeSendStatus.Success });
    } catch (err) {
      stopCountDown(payload);
      return Promise.reject({ msg: err?.message || '获取验证码失败，请稍后重试', status: VerCodeSendStatus.Failed });
    } finally {
      setLoading(false);
    }
  };

  async function sendCodeToPhone(payload: LoginInterface & { scope?: number }) {
    if (running) return;
    setLoading(true);
    stopCountDown(payload);
    try {
      const { code, message: msg } = await service.userSetting.sendCode(payload);
      if (code === FastApiStatusCode.CodeHasBeenSent) {
        startCountDown(payload);
        setLoading(false);
        return Promise.resolve({ msg: '该手机号验证码已发送', status: VerCodeSendStatus.TooFrequent });
      }
      startCountDown(payload);
      setLoading(false);
      return Promise.resolve({ msg, status: VerCodeSendStatus.Success });
    } catch (err) {
      stopCountDown(payload);
      return Promise.reject({ msg: err?.message || '获取验证码失败，请稍后重试', status: VerCodeSendStatus.Failed });
    } finally {
      setLoading(false);
    }
  }
  return { running, countdown, formatted, sendCode, stop: stopCountDown, sendCodeToPhone };
};
